import { useState, useEffect } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from './useAuth';
import type { Quotation } from '@/types/invoice';
import { useToast } from '@/components/ui/use-toast';
import { generateQuotationNumber } from '@/lib/utils';

export function useQuotation() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch all quotations
  const fetchQuotations = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const quotationsRef = collection(db, 'users', user.uid, 'quotations');
      const q = query(quotationsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const quotationsList = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          quotationId: doc.id,
          issueDate: data.issueDate?.toDate(),
          validUntil: data.validUntil?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Quotation;
      });

      setQuotations(quotationsList);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load quotations',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Get next quotation number
  const getNextQuotationNumber = async (prefix: 'QT' | 'EST'): Promise<string> => {
    if (!user) return `${prefix}-0001`;

    try {
      const quotationsRef = collection(db, 'users', user.uid, 'quotations');
      const q = query(quotationsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return `${prefix}-0001`;
      }

      // Find the last quotation that matches the requested prefix
      const matchDoc = snapshot.docs.find(doc => doc.data().quotationNumber?.startsWith(prefix));
      if (!matchDoc) {
        return `${prefix}-0001`;
      }

      const lastQuotation = matchDoc.data();
      return generateQuotationNumber(lastQuotation.quotationNumber, prefix);
    } catch (error) {
      console.error('Error getting next quotation number:', error);
      return `${prefix}-0001`;
    }
  };

  // Create quotation
  const createQuotation = async (quotationData: Omit<Quotation, 'quotationId' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const quotationsRef = collection(db, 'users', user.uid, 'quotations');
      const docRef = await addDoc(quotationsRef, {
        ...quotationData,
        issueDate: Timestamp.fromDate(quotationData.issueDate),
        validUntil: Timestamp.fromDate(quotationData.validUntil),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      toast({
        title: 'Quotation Created',
        description: `Quotation ${quotationData.quotationNumber} has been created.`,
      });

      await fetchQuotations();
      return docRef.id;
    } catch (error) {
      console.error('Error creating quotation:', error);
      toast({
        title: 'Error',
        description: 'Failed to create quotation',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Update quotation
  const updateQuotation = async (quotationId: string, quotationData: Partial<Quotation>): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const quotationRef = doc(db, 'users', user.uid, 'quotations', quotationId);
      const updateData: any = { ...quotationData, updatedAt: Timestamp.now() };

      if (quotationData.issueDate) {
        updateData.issueDate = Timestamp.fromDate(quotationData.issueDate);
      }
      if (quotationData.validUntil) {
        updateData.validUntil = Timestamp.fromDate(quotationData.validUntil);
      }

      await updateDoc(quotationRef, updateData);

      toast({
        title: 'Quotation Updated',
        description: 'Quotation has been updated successfully.',
      });

      await fetchQuotations();
    } catch (error) {
      console.error('Error updating quotation:', error);
      toast({
        title: 'Error',
        description: 'Failed to update quotation',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Delete quotation
  const deleteQuotation = async (quotationId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const quotationRef = doc(db, 'users', user.uid, 'quotations', quotationId);
      await deleteDoc(quotationRef);

      toast({
        title: 'Quotation Deleted',
        description: 'Quotation has been deleted successfully.',
      });

      await fetchQuotations();
    } catch (error) {
      console.error('Error deleting quotation:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete quotation',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Get single quotation
  const getQuotation = async (quotationId: string): Promise<Quotation | null> => {
    if (!user) return null;

    try {
      const quotationRef = doc(db, 'users', user.uid, 'quotations', quotationId);
      const quotationDoc = await getDoc(quotationRef);

      if (quotationDoc.exists()) {
        const data = quotationDoc.data();
        return {
          ...data,
          quotationId: quotationDoc.id,
          issueDate: data.issueDate?.toDate(),
          validUntil: data.validUntil?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Quotation;
      }
      return null;
    } catch (error) {
      console.error('Error fetching quotation:', error);
      return null;
    }
  };

  // Load quotations on user state change
  useEffect(() => {
    if (user) {
      fetchQuotations();
    } else {
      setQuotations([]);
    }
  }, [user]);

  return {
    quotations,
    loading,
    fetchQuotations,
    getNextQuotationNumber,
    createQuotation,
    updateQuotation,
    deleteQuotation,
    getQuotation,
  };
}
